import { z } from 'zod';

import { buildOfferId } from './catalog.js';
import { listQuerySchema } from './common.js';
import {
  catalogDestinationKindSchema,
  connectionStatusSchema,
  destinationItemStateSchema,
  merchantAccountKindSchema,
  type CatalogDestinationKind,
  type ConnectionStatus,
  type DestinationItemState,
  type MerchantAccountKind,
} from './enums.js';

/**
 * KATALOG HEDEFLERİ — katalogun dışarıya yayınlanması (19 Ağu 2026).
 *
 * İlk ve bugün tek hedef: Google Merchant Center.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Bu dosya neden `commerce.ts`ten AYRI
 * ═══════════════════════════════════════════════════════════════════════════
 * `commerce.ts` mağazanın OKUNMASINI anlatıyor: bağlan, ürün çek, sipariş çek,
 * webhook dinle. Buradaki kavramların hiçbirinin orada karşılığı yok —
 * "data source", "teklif kimliği", "hedefteki onay durumu", "çakışan feed".
 * Ters yön de doğru: hedefin siparişi, stok seviyesi, webhook'u yok.
 *
 * Aynı dosyaya koymak, alanlarının yarısı yalnız bir yönde anlamlı olan bir
 * sözleşme üretirdi — `video.ts`/`studio.ts`nin `creative.ts`ten ayrılma
 * gerekçesiyle birebir aynı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 "Hedef" iki farklı şey demek — bu dosya YALNIZ birincisi
 * ═══════════════════════════════════════════════════════════════════════════
 *   KATALOG hedefi (burası)   → ürün SATIRI gönderiyoruz (GMC, ileride Meta Catalog)
 *   OLAY hedefi (henüz yok)   → dönüşüm OLAYI gönderiyoruz (Meta CAPI, GA4, Google EC)
 *
 * İkisi ortak bir soyutlamaya SOKULMAYACAK. Girdileri farklı (ürün tablosu vs
 * olay akışı), kimlik gereksinimleri farklı (biri kişisel veri istemiyor,
 * diğeri hash'lenmiş e-posta/telefon şart), hata anlamları farklı. Bugün
 * birleştirmek, ikisini de yanlış modelleyen tek bir arayüz doğururdu.
 */

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 OAuth — geri dönüş adresi ÖLÇÜLDÜ, tahmin edilmedi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Merchant Center izin kapsamları.
 *
 * 🔴 `GOOGLE_ADS_SCOPES`E EKLENMEDİ ve eklenmeyecek. Gerekçe
 * `integrations.ts`teki `leads_retrieval` notunun aynısı: bir kapsam listesine
 * ekleme yapmak zaten bağlı hesapların jetonunu geçersiz KILMIYOR, ama o
 * jetonda yeni kapsam da YOK — yani bugün Google Ads'e bağlı her kullanıcı,
 * yeniden onay verene kadar Merchant Center'ı bağlayamamış ama arayüzde
 * bağlıymış gibi görünürdü.
 *
 * Ayrı bağlantı satırı bunu imkânsız kılıyor: Merchant bağlantısı ya var ya
 * yok, yarım hâli yok.
 *
 * `openid` yine ZORUNLU ve yine süs değil: hesabın kararlı kimliğini
 * (`tokeninfo.sub`) oradan okuyoruz ve Google `sub`u yalnız OpenID kapsamı
 * verilmiş jetonlarda döndürüyor. Ads tarafında bu tam olarak bir kez
 * kırılmıştı (14 Ağu 2026) — aynı hataya ikinci kez düşülmesin.
 *
 * ⚠️ `content` kapsamı okuma ve YAZMAYI ayırmıyor; Google'ın "yalnız ürün
 * durumunu okuyayım" diye bir kapsamı yok. İzin ekranı kaçınılmaz olarak
 * yazma da istiyor ve bunu kullanıcıya söylemek gerekiyor.
 */
export const GOOGLE_MERCHANT_SCOPES = [
  'openid',
  'https://www.googleapis.com/auth/content',
] as const;

/**
 * Hedef OAuth geri dönüş yolu.
 *
 * 🔴 `/api/v1` ÖNEKİ BİLİNÇLİ ve ölçülmüş. 19 Ağu 2026'da Google izin ucuna
 * kontrol gruplu bir sorgu atıldı:
 *
 *     .../api/v1/integrations/google/callback            → giriş ekranı  (kayıtlı)
 *     .../kesinlikle-yok-12345/callback                  → redirect_uri_mismatch
 *     .../api/v1/integrations/google-merchant/callback   → giriş ekranı  (KAYITLI)
 *     .../integrations/google-merchant/callback          → redirect_uri_mismatch
 *
 * Yani öneksiz biçim Google'da KAYITLI DEĞİL. OAuth istemcisi eski APEX
 * GrowthOS kurulumundan devralındığı için o projenin kaydı hâlâ geçerli;
 * Console'da yapılacak iş yok ama önek de kaldırılamaz.
 *
 * Controller iki yolu da dinliyor (`@Controller(['integrations',
 * 'api/v1/integrations'])`) — sorun GELEN istekte değil, GİDEN adresin
 * birebir eşleşmesinde.
 */
export const DESTINATION_CALLBACK_PATH = (kind: string) => `/api/v1/integrations/${kind}/callback`;

/// Rota parçası ↔ enum. Arayüz ve controller aynı sözlüğü okuyor ki
/// `parsePlatform()` tipi bir "eklemeyi unutulan satır" doğmasın.
export const DESTINATION_SLUG: Record<CatalogDestinationKind, string> = {
  GOOGLE_MERCHANT: 'google-merchant',
};

export const DESTINATION_KIND_BY_SLUG: Record<string, CatalogDestinationKind> = {
  'google-merchant': 'GOOGLE_MERCHANT',
};

/// Hesap keşfi önbelleği — ekran her açılışta Merchant API kotası yakmasın.
export const DESTINATION_DISCOVERY_CACHE_SECONDS = 10 * 60;

/**
 * Elle yayın sıklık sınırı — hesap başına.
 *
 * Mağaza senkronundan (5 dk) UZUN çünkü yayın YAZMA: Google tarafında her
 * koşu yeni bir işleme kuyruğu doğuruyor ve arka arkaya tetiklemek ürünlerin
 * `PENDING`te daha uzun kalmasına yol açıyor.
 */
export const DESTINATION_PUBLISH_MIN_INTERVAL_SECONDS = 30 * 60;

/**
 * Bir teklif tazelenmezse Google'da düşüyor. Zamanlanmış yayının sıklığı
 * (6 saat) bu pencerenin çok altında; sabit, `EXPIRING` uyarısının ne kadar
 * erken anlamlı olduğunu söylemek için burada.
 */
export const MERCHANT_OFFER_TTL_DAYS = 30;

// ═══════════════════════════════════════════════════════════════════════════
// Adapter sınırı — üst katmanların gördüğü şekiller
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Hedefin neyi destekleyip neyi desteklemediği. Arayüz düğmeyi buna göre
 * çiziyor; "destekleniyor mu" sorusunun cevabı koda gömülü olmuyor.
 *
 * `ConnectorCapabilities` (reklam) ile aynı üç durum mantığı: `available`
 * env'i de yansıtıyor, anahtarlar boşsa `false`.
 */
export interface DestinationCapabilities {
  kind: CatalogDestinationKind;
  available: boolean;
  /// `available: false` ise nedeni — i18n anahtarı, düz metin değil.
  unavailableReasonKey: string | null;
  /// Ürün satırı yazabiliyor mu (feed yayını).
  canPublish: boolean;
  /// Ürün başına onay durumu ve ret kodu okuyabiliyor mu.
  canReadItemStatus: boolean;
  /// Gösterim/tıklama raporu verebiliyor mu (organik dâhil).
  canReadPerformance: boolean;
  /**
   * Hedef, çağrı yapmadan önce bir kayıt adımı istiyor mu.
   *
   * GMC'de `true`: kayıtsız bir GCP projesinden gelen HER çağrı 401 dönüyor
   * ve dönen hata "jeton geçersiz" gibi okunuyor. Arayüzün bu adımı ayrı bir
   * düğme olarak göstermesi gerekiyor, yoksa kullanıcı jetonunu suçlar.
   */
  requiresDeveloperRegistration: boolean;
}

/**
 * Platformdan keşfedilen Merchant hesabı (henüz seçilmemiş olabilir).
 *
 * `DiscoveredAccount` (reklam) ile aynı desen; `alreadyConnected` yine çağıran
 * tarafta dolduruluyor — connector bizim veritabanımızı bilmiyor.
 */
export interface DiscoveredMerchantAccount {
  merchantId: string;
  name: string;
  kind: MerchantAccountKind;
  /// Hesaba tanımlı doğrulanmış site. `null` → ürün onayı ALINAMAZ ve arayüz
  /// bunu söylemeli; feed basmak bu hâlde boşa iş.
  websiteUrl: string | null;
  country: string | null;
  currency: string | null;
  /**
   * `ADVANCED` hesaplar ürün TAŞIMIYOR — Google raporu ve ürün uçlarını
   * yalnız standalone ve alt hesaplara açıyor. Arayüz o satırı seçilemez
   * gösteriyor; seçtirip sonra hata döndürmek kullanıcıya bir şey öğretmez.
   */
  selectable: boolean;
  alreadyConnected: boolean;
}

/**
 * Hedeften okunan bir ürün satırı (worker sınırı).
 *
 * ⚠️ Bu bir ÜRÜN DEĞİL, bir DURUM. Bizim `products` tablomuza yazılmıyor;
 * `MerchantProductStatus`a gidiyor. Hedefteki ürün, bizim bastığımızın
 * kopyası — onu kaynak saymak, hedefi bir mağazaymış gibi modellemek olurdu.
 */
export interface NormalizedMerchantItem {
  offerId: string;
  title: string | null;
  state: DestinationItemState;
  /// `{ "Shopping ads": "APPROVED", "Free listings": "DISAPPROVED" }`
  destinations: Record<string, string>;
  /// Google'ın item-level issue KODLARI (`missing_gtin`, `image_link_broken`…).
  /// Kullanıcıya gösterilen metin değil — çeviri arayüzde.
  issueCodes: string[];
}

/**
 * Hedeften okunan bir günlük performans satırı.
 *
 * 🔴 `clicks`/`impressions` ORGANİK VE ÜCRETLİ AYRI. Google
 * `product_performance_view`da bunu `marketingMethod` boyutuyla veriyor ve
 * toplamak bilgi kaybı olurdu: ürünün ücretsiz listelemeden aldığı tıklama,
 * reklam bütçesinden bağımsız bir sinyal — "reklamı kessem ne olur"
 * sorusunun tek cevabı o.
 *
 * `null` = platform o satırda vermedi; `0` = verdi ve sıfırdı. Ayrım
 * `NormalizedInsight`taki `reach`/`frequency` ile aynı gerekçeyle korunuyor.
 */
export interface NormalizedDestinationInsight {
  offerId: string;
  /// `YYYY-MM-DD`.
  date: string;
  organicClicks: number | null;
  organicImpressions: number | null;
  paidClicks: number | null;
  paidImpressions: number | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Yayın — kuru koşu ve eleme sebepleri
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bir ürünün NEDEN yayınlanmadığı.
 *
 * 🔴 Bu liste bir hata listesi DEĞİL, bir BEYAN listesi. Kuru koşuda
 * kullanıcıya gösteriliyor ve her sebep ayrı sayılıyor: "412 üründen 38'i
 * gitmedi" cümlesi tek başına işe yaramaz, "31'inin görseli yok, 5'i zaten
 * Shopify feed'inde, 2'sinin kimliği taşınamıyor" cümlesi eyleme dönüşür.
 */
export const PUBLISH_SKIP_REASON = {
  /// Ürün mağazada yayında değil (DRAFT / ARCHIVED).
  NOT_ACTIVE: 'NOT_ACTIVE',
  /// Ürünün hiç varyantı yok — fiyat ve kimlik varyanttan geliyor.
  NO_VARIANTS: 'NO_VARIANTS',
  /// `onlineStoreUrl` yok ve mağaza alan adından da türetilemiyor.
  MISSING_LINK: 'MISSING_LINK',
  MISSING_IMAGE: 'MISSING_IMAGE',
  /// Fiyat yok ya da sıfır. Google sıfır fiyatlı teklifi reddediyor.
  MISSING_PRICE: 'MISSING_PRICE',
  /**
   * `buildOfferId()` güvenli bir kimlik üretemedi (Latin dışı karakter,
   * 50 karakter aşımı, boş `platformRef`).
   *
   * Bu ürün BASILMIYOR — kırpıp ya da temizleyip basmak, Google'da yayında
   * ama raporumuzda hiç var olmayan bir teklif üretirdi.
   */
  OFFER_ID_UNSAFE: 'OFFER_ID_UNSAFE',
  /// İki varyant aynı teklif kimliğini üretti (yalnız büyük/küçük harfle
  /// ayrışan `platformRef`ler). Hangisinin gideceği bilinmiyor → ikisi de
  /// gitmiyor.
  OFFER_ID_DUPLICATE: 'OFFER_ID_DUPLICATE',
  /**
   * Aynı teklif kimliği hedefte ZATEN VAR ve onu biz basmadık.
   *
   * 🔴 Sessiz hasarın çıkacağı tek yer buydu: Google ürünü
   * `contentLanguage~feedLabel~offerId` ile kimliklendiriyor. Farklı bir
   * `feedLabel` altında aynı ürünü basmak onu İKİNCİ bir teklif yapardı ve
   * iki teklif aynı açık artırmada birbiriyle yarışırdı. Kullanıcı bunu fark
   * etmez, yalnız performansı düşer.
   */
  EXTERNAL_FEED_CONFLICT: 'EXTERNAL_FEED_CONFLICT',
} as const;
export type PublishSkipReason = (typeof PUBLISH_SKIP_REASON)[keyof typeof PUBLISH_SKIP_REASON];

// ═══════════════════════════════════════════════════════════════════════════
// Yayın planlayıcısı — SAF ve TEK KOPYA
// ═══════════════════════════════════════════════════════════════════════════
//
// 🔴 Bu bölüm neden `apps/api`de ya da `apps/worker`da DEĞİL
//
// Yayını iki taraf da hesaplıyor: API kuru koşuyu (kullanıcıya kaç ürünün
// gideceğini göstermek için), worker gerçek koşuyu. İki taraf ayrı derleme
// birimi ve birbirini import edemiyor — yani mantık iki yere yazılsaydı
// SAPMASI kaçınılmazdı ve sapma SESSİZ olurdu: önizleme "412 ürün gidecek"
// der, yayın 380 gönderirdi ve fark hiçbir yerde görünmezdi.
//
// Bu tam olarak deponun daha önce ölçtüğü hata: `sozlesme-vs-gerceklik`
// notundaki "listeyi elle kopyalama" ve `gaqlString`in beş kopyası. Burada
// baştan tek kopya.
//
// Platformdan BAĞIMSIZ tutuldu: Google'a özgü serileştirme (mikro fiyat,
// `gtins`, alan adları) adapter'da. Buradaki karar "bu varyant yayınlanabilir
// mi ve kimliği ne" — Meta Catalog geldiğinde aynı cevabı verecek soru.

export interface PublishCandidateProductRow {
  id: string;
  platformRef: string;
  title: string;
  description: string | null;
  productType: string | null;
  vendor: string | null;
  /// `ProductStatus` — mağazadaki yayın durumu.
  status: string;
  imageUrl: string | null;
  onlineStoreUrl: string | null;
  variantCount: number;
}

export interface PublishCandidateVariantRow {
  id: string;
  productId: string;
  platformRef: string;
  sku: string | null;
  barcode: string | null;
  price: number;
  compareAtPrice: number | null;
  /// `null` = stok takibi yok, `0` = tükendi. İKİSİ AYRI ŞEY.
  inventoryQuantity: number | null;
  imageUrl: string | null;
}

export interface PublishPlanContext {
  currency: string;
  /// Bileşik teklif kimliğinin ülke segmenti (Shopify kaynaklı ürünlerde).
  feedCountry: string | null;
}

/**
 * Platformdan bağımsız, yayına HAZIR teklif.
 *
 * Adapter bunu kendi şekline çeviriyor. Buradaki `price`/`salePrice` ayrımı
 * Google'ın sözleşmesine göre: `price` NORMAL fiyat, `salePrice` indirimli
 * olan. Mağazadaki `compareAtPrice` indirim ÖNCESİ fiyat, yani ters yönde —
 * çeviri burada bir kez yapılıyor.
 */
export interface NormalizedOffer {
  offerId: string;
  productId: string;
  variantId: string;
  title: string;
  description: string | null;
  link: string;
  imageLink: string;
  brand: string | null;
  productType: string | null;
  price: number;
  /// İndirim yoksa `null`.
  salePrice: number | null;
  currency: string;
  availability: 'in_stock' | 'out_of_stock';
  /// Barkod. Yoksa `null` → adapter `identifierExists: false` gönderiyor.
  gtin: string | null;
  /// Yalnız ÇOK varyantlı üründe dolu.
  itemGroupId: string | null;
}

export type PublishCandidateResult =
  | { ok: true; offer: NormalizedOffer }
  | { ok: false; reason: PublishSkipReason; detail: string | null };

/**
 * Tek bir varyantın yayınlanabilirliği.
 *
 * 🔴 EKSİK VERİ UYDURULMUYOR. Reddedilen alternatif: `link`i mağaza alan adı
 * ve `handle`dan türetmek, görsel yoksa yer tutucu koymak. Türetilen bağlantı
 * Google'ın tarayıcısı tarafından ziyaret ediliyor ve tutmazsa ürün YİNE
 * yayınlanmıyor — ama bu kez sebebi bizim ekranımızda değil Google'ın
 * panelinde görünüyor. Elemek aynı sonucu ÖNCEDEN ve doğru yerde söylüyor.
 */
export function evaluatePublishCandidate(
  product: PublishCandidateProductRow,
  variant: PublishCandidateVariantRow,
  ctx: PublishPlanContext,
): PublishCandidateResult {
  if (product.status !== 'ACTIVE') {
    return { ok: false, reason: PUBLISH_SKIP_REASON.NOT_ACTIVE, detail: product.status };
  }

  const link = absoluteHttpUrl(product.onlineStoreUrl);
  if (!link) return { ok: false, reason: PUBLISH_SKIP_REASON.MISSING_LINK, detail: null };

  const imageLink = absoluteHttpUrl(variant.imageUrl) ?? absoluteHttpUrl(product.imageUrl);
  if (!imageLink) return { ok: false, reason: PUBLISH_SKIP_REASON.MISSING_IMAGE, detail: null };

  if (!Number.isFinite(variant.price) || variant.price <= 0) {
    return { ok: false, reason: PUBLISH_SKIP_REASON.MISSING_PRICE, detail: null };
  }

  const built = buildOfferId({
    productPlatformRef: product.platformRef,
    variantPlatformRef: variant.platformRef,
    feedCountry: ctx.feedCountry,
  });
  if (!built.ok) {
    return { ok: false, reason: PUBLISH_SKIP_REASON.OFFER_ID_UNSAFE, detail: built.reason };
  }

  /**
   * `compareAtPrice` indirim ÖNCESİ fiyat; Google'ın `price`i de o.
   * Karşılaştırma fiyatı DÜŞÜKSE (mağazada yanlış girilmiş) indirim yokmuş
   * gibi davranıyoruz — `catalog-health.ts`teki `MISLEADING_COMPARE_AT_PRICE`
   * kuralı o durumu ayrıca raporluyor.
   */
  const onSale =
    variant.compareAtPrice !== null &&
    Number.isFinite(variant.compareAtPrice) &&
    variant.compareAtPrice > variant.price;

  return {
    ok: true,
    offer: {
      offerId: built.offerId,
      productId: product.id,
      variantId: variant.id,
      title: product.title,
      description: product.description,
      link,
      imageLink,
      brand: product.vendor,
      productType: product.productType,
      price: onSale ? (variant.compareAtPrice as number) : variant.price,
      salePrice: onSale ? variant.price : null,
      currency: ctx.currency,
      availability: toOfferAvailability(variant.inventoryQuantity),
      gtin: variant.barcode?.trim() || null,
      itemGroupId: product.variantCount > 1 ? product.id : null,
    },
  };
}

/**
 * Stok sayısı → satılabilirlik.
 *
 * 🔴 `null` ile `0` AYRI: `null` "bu mağaza stok takip etmiyor" demek ve o
 * ürün satışta. `0` yazsaydık takip etmeyen her mağazanın katalogunun tamamı
 * tükenmiş görünürdü — `catalog-health.ts`teki `OUT_OF_STOCK` kuralının
 * denetimde yakalanan hatasının birebir aynısı (`[].every()` `true` döner).
 */
export function toOfferAvailability(inventoryQuantity: number | null): 'in_stock' | 'out_of_stock' {
  if (inventoryQuantity === null) return 'in_stock';
  return inventoryQuantity > 0 ? 'in_stock' : 'out_of_stock';
}

// ═══════════════════════════════════════════════════════════════════════════
// Katalog satırı → yayın adayı (SAF)
// ═══════════════════════════════════════════════════════════════════════════
//
// 🔴 Bu bölüm de tek kopya olmak zorunda: adayları API (kuru koşu) ve worker
// (gerçek yayın) AYNI `SELECT`le okumak zorunda. İki ayrı sorgu, önizlemenin
// ve yayının farklı ürün kümesi görmesi demekti — ve fark hiçbir yerde
// görünmezdi.
//
// Prisma'nın `select` nesnesi düz JSON; paylaşılabiliyor. Satırları tipe
// çeviren fonksiyon da burada.

/**
 * Yayın adaylarının Prisma `select` şekli.
 *
 * ⚠️ `as const` ZORUNLU: `const` olmadan Prisma alan adlarını `boolean` diye
 * genelleştiriyor ve dönen satırın tipi `any`ye düşüyor.
 */
export const PUBLISH_CANDIDATE_SELECT = {
  id: true,
  platformRef: true,
  title: true,
  description: true,
  productType: true,
  vendor: true,
  status: true,
  imageUrl: true,
  onlineStoreUrl: true,
  connection: { select: { currency: true } },
  variants: {
    orderBy: { position: 'asc' },
    select: {
      id: true,
      productId: true,
      platformRef: true,
      sku: true,
      barcode: true,
      price: true,
      compareAtPrice: true,
      inventoryQuantity: true,
      imageUrl: true,
    },
  },
} as const;

/**
 * Ham satır — Prisma sonucunun YAPISAL karşılığı.
 *
 * `price` `unknown` çünkü Prisma `Decimal` döndürüyor ve o tip
 * `@mentorivo/db`de; shared onu import EDEMEZ (uygulama katmanına bağımlılık
 * olurdu). `Number()` `Decimal` üzerinde çalışıyor (`valueOf` var) ve
 * dönüşüm tek yerde.
 */
export interface RawCandidateVariantRow {
  id: string;
  productId: string;
  platformRef: string;
  sku: string | null;
  barcode: string | null;
  price: unknown;
  compareAtPrice: unknown;
  inventoryQuantity: number | null;
  imageUrl: string | null;
}

export interface RawCandidateRow {
  id: string;
  platformRef: string;
  title: string;
  description: string | null;
  productType: string | null;
  vendor: string | null;
  status: string;
  imageUrl: string | null;
  onlineStoreUrl: string | null;
  connection: { currency: string };
  variants: RawCandidateVariantRow[];
}

export interface PublishCandidateSet {
  products: PublishCandidateProductRow[];
  variantsByProduct: Map<string, PublishCandidateVariantRow[]>;
  /// Mağaza bağlantısının para birimi — varyantta para birimi kolonu YOK.
  currency: string;
}

/**
 * Ham satırları yayın adaylarına çevirir.
 *
 * ⚠️ Para birimi İLK bağlantıdan. Çok mağazalı bir kiracıda mağazaların para
 * birimi farklı olabilir ve Google tek feed'de tek para birimi bekliyor. Bu
 * yüzden yayın isteğinde mağaza seçimi var; seçilmediğinde bu bir VARSAYIM.
 * Karışık para birimli katalogda doğru davranış mağaza başına ayrı feed —
 * ikinci mağaza gerçekten geldiğinde ölçülüp yapılacak, şimdi tahminle
 * yazılmayacak.
 */
export function toPublishCandidateSet(rows: RawCandidateRow[]): PublishCandidateSet {
  const products: PublishCandidateProductRow[] = [];
  const variantsByProduct = new Map<string, PublishCandidateVariantRow[]>();

  for (const row of rows) {
    products.push({
      id: row.id,
      platformRef: row.platformRef,
      title: row.title,
      description: row.description,
      productType: row.productType,
      vendor: row.vendor,
      status: row.status,
      imageUrl: row.imageUrl,
      onlineStoreUrl: row.onlineStoreUrl,
      variantCount: row.variants.length,
    });
    variantsByProduct.set(
      row.id,
      row.variants.map((v) => ({
        id: v.id,
        productId: v.productId,
        platformRef: v.platformRef,
        sku: v.sku,
        barcode: v.barcode,
        price: Number(v.price),
        compareAtPrice: v.compareAtPrice === null ? null : Number(v.compareAtPrice),
        inventoryQuantity: v.inventoryQuantity,
        imageUrl: v.imageUrl,
      })),
    );
  }

  return { products, variantsByProduct, currency: rows[0]?.connection.currency ?? 'TRY' };
}

/// Hedefte hâlihazırda duran bir teklif.
export interface TargetOfferRef {
  offerId: string;
  /// Bizim bastığımız mı. `false` → kullanıcının başka bir feed'i.
  ownedByUs: boolean;
  /// Çakışmada kullanıcıya gösterilecek kaynak adı.
  sourceName: string | null;
}

export interface PublishPlan {
  /// Hedefte hiç olmayan, ilk kez basılacaklar.
  create: NormalizedOffer[];
  /// Bizim bastığımız ve tazelenecek olanlar.
  update: NormalizedOffer[];
  /// Bizim bastığımız ama artık katalogda olmayanlar.
  deleteOfferIds: string[];
  skipped: PublishSkipRow[];
  skippedByReason: Record<PublishSkipReason, number>;
  /// Hedefte olup BİZİM basmadığımız teklif sayısı.
  foreignOffers: number;
}

/**
 * Katalog + hedefin mevcut hâli → ne yapılacağı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 ÇAKIŞMA KURALI — sessiz hasarın çıkacağı tek yer
 * ═══════════════════════════════════════════════════════════════════════════
 * Google ürünü `contentLanguage~feedLabel~offerId` üçlüsüyle kimliklendiriyor.
 * Kullanıcının hesabında zaten bir feed olabilir (Shopify'ın kendi Google
 * kanalı, elle yüklenen dosya, scheduled fetch). Aynı ürünü FARKLI bir
 * `feedLabel` altında basmak onu İKİNCİ bir teklif yapar ve iki teklif aynı
 * açık artırmada birbiriyle yarışır. Kullanıcı bunu fark etmez, yalnız
 * performansı düşer.
 *
 * Bu yüzden: `offerId`si hedefte VAR ve BİZİM DEĞİLSE → basılmıyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 SİLME YALNIZ BİZİM BASTIKLARIMIZDA
 * ═══════════════════════════════════════════════════════════════════════════
 * `deleteOfferIds` yalnız `ownedByUs: true` olanlardan doluyor. Kullanıcının
 * başka bir aracının feed'ini "temizlik" adına silmek, bir `deleteMany`
 * uzaklığındaydı ve geri alınamazdı.
 *
 * ⚠️ Küçük harf çakışması ayrıca ele alınıyor: iki varyant yalnız büyük/küçük
 * harfle ayrışan `platformRef` taşıyorsa aynı `offerId`yi üretirler. Hangisinin
 * gideceği bilinmiyor demektir; İKİSİ DE gitmiyor (`ambiguousSkus` kuralının
 * aynısı — harcamayı yazı turayla bölüştürmüyoruz).
 */
export function planPublish(
  products: PublishCandidateProductRow[],
  variantsByProduct: Map<string, PublishCandidateVariantRow[]>,
  targetOffers: Map<string, TargetOfferRef>,
  ctx: PublishPlanContext,
): PublishPlan {
  const skipped: PublishSkipRow[] = [];
  const byOfferId = new Map<string, { offer: NormalizedOffer; title: string }[]>();

  for (const product of products) {
    const variants = variantsByProduct.get(product.id) ?? [];
    if (variants.length === 0) {
      skipped.push({
        productId: product.id,
        variantId: null,
        title: product.title,
        reason: PUBLISH_SKIP_REASON.NO_VARIANTS,
        detail: null,
      });
      continue;
    }

    for (const variant of variants) {
      const result = evaluatePublishCandidate(product, variant, ctx);
      if (!result.ok) {
        skipped.push({
          productId: product.id,
          variantId: variant.id,
          title: product.title,
          reason: result.reason,
          detail: result.detail,
        });
        continue;
      }
      const bucket = byOfferId.get(result.offer.offerId) ?? [];
      bucket.push({ offer: result.offer, title: product.title });
      byOfferId.set(result.offer.offerId, bucket);
    }
  }

  const create: NormalizedOffer[] = [];
  const update: NormalizedOffer[] = [];
  const planned = new Set<string>();
  /**
   * Belirsiz kimlikler — basılMIYOR ama SİLİNMİYOR da.
   *
   * 🔴 Silmemek ayrı bir karar ve gerekçesi şu: ürün katalogda VAR, yalnız
   * hangi varyanttan geldiği bilinmiyor. Silseydik hedefte yayında olan
   * gerçek bir teklif kalkardı ve bir sonraki koşuda geri gelmezdi
   * (belirsizlik sürüyor) — yani ürün Google'dan SESSİZCE düşerdi.
   * "Belirsizlikte hiçbir şey yapma" kuralı her iki yönde de geçerli.
   */
  const ambiguous = new Set<string>();

  for (const [offerId, bucket] of byOfferId) {
    if (bucket.length > 1) {
      ambiguous.add(offerId);
      // Aynı kimliği üreten birden çok varyant — hepsi eleniyor.
      for (const b of bucket) {
        skipped.push({
          productId: b.offer.productId,
          variantId: b.offer.variantId,
          title: b.title,
          reason: PUBLISH_SKIP_REASON.OFFER_ID_DUPLICATE,
          detail: offerId,
        });
      }
      continue;
    }

    const only = bucket[0];
    if (!only) continue;

    const existing = targetOffers.get(offerId);
    if (existing && !existing.ownedByUs) {
      skipped.push({
        productId: only.offer.productId,
        variantId: only.offer.variantId,
        title: only.title,
        reason: PUBLISH_SKIP_REASON.EXTERNAL_FEED_CONFLICT,
        detail: existing.sourceName,
      });
      continue;
    }

    planned.add(offerId);
    if (existing) update.push(only.offer);
    else create.push(only.offer);
  }

  const deleteOfferIds: string[] = [];
  for (const [offerId, ref] of targetOffers) {
    if (!ref.ownedByUs) continue;
    if (planned.has(offerId)) continue;
    if (ambiguous.has(offerId)) continue;
    deleteOfferIds.push(offerId);
  }

  const foreignOffers = [...targetOffers.values()].filter((o) => !o.ownedByUs).length;

  return {
    create,
    update,
    deleteOfferIds,
    skipped,
    skippedByReason: countByReason(skipped),
    foreignOffers,
  };
}

/// Sebep sayacı — arayüz listeyi açmadan sayıyı gösterebilsin. Her sebep
/// SIFIRLA başlıyor: eksik anahtar, "bu sebep hiç oluşmadı" ile "bu sebebi
/// hiç saymadık" arasındaki farkı siler.
export function countByReason(rows: PublishSkipRow[]): Record<PublishSkipReason, number> {
  const out = Object.fromEntries(Object.values(PUBLISH_SKIP_REASON).map((r) => [r, 0])) as Record<
    PublishSkipReason,
    number
  >;
  for (const row of rows) out[row.reason] += 1;
  return out;
}

/// Mutlak `http(s)` adresi mi. Değilse `null` — göreli yol Google'a
/// gönderilemez ve `javascript:` gibi bir şema hiç gönderilmemeli.
function absoluteHttpUrl(value: string | null | undefined): string | null {
  const v = value?.trim();
  if (!v) return null;
  return /^https?:\/\//i.test(v) ? v : null;
}

export interface PublishSkipRow {
  productId: string;
  variantId: string | null;
  title: string;
  reason: PublishSkipReason;
  /// `EXTERNAL_FEED_CONFLICT`te çakışılan data source'un adı; diğerlerinde
  /// `OFFER_ID_UNSAFE`in alt sebebi (`TOO_LONG`…). Yoksa `null`.
  detail: string | null;
}

/**
 * Kuru koşu sonucu — kullanıcı bunu onaylamadan tek satır gitmiyor.
 *
 * İlk yayında zorunlu, sonrakilerde de varsayılan. Gerekçe: ilk koşu bir
 * katalogun tamamını Google'a gönderiyor ve geri alması (silmek) ayrı bir iş.
 */
export interface PublishPreviewDto {
  merchantAccountId: string;
  /// Basılacak teklif sayısı.
  willPublish: number;
  /// Halihazırda bizim bastığımız ve güncellenecek olanlar.
  willUpdate: number;
  /// Bizim bastığımız ama artık katalogda olmayan → silinecek.
  willDelete: number;
  skipped: PublishSkipRow[];
  /// Sebebe göre özet — arayüz listeyi açmadan sayıyı gösterebilsin.
  skippedByReason: Record<PublishSkipReason, number>;
  /// Hedefte var olan ama BİZİM basmadığımız teklif sayısı. Sıfırdan büyükse
  /// kullanıcının başka bir feed'i var demek ve bu ekranda söylenmeli.
  foreignOffers: number;
  /// Kuru koşunun kendisi Merchant API'ye gitti mi. `false` → hedef
  /// okunamadı, sayılar yalnız katalog tarafını yansıtıyor.
  targetRead: boolean;
  /**
   * Hedefteki teklif listesi TAM okunabildi mi.
   *
   * 🔴 Sessiz kısaltma yasak. Liste sınıra takıldıysa çakışma tespiti eksik
   * demektir ve arayüz bunu söylemek zorunda — yoksa kullanıcı "çakışma yok"
   * diye okur ve aynı ürünü ikinci kez yayınlar.
   */
  targetTruncated: boolean;
}

/// Yayın tetikleme yanıtı. `StoreSyncTriggerResult` ile aynı desen ve aynı
/// uyarı: `queued: true` "yayın bitti" değil, "iş kuyruğa girdi" demek.
export interface PublishTriggerResult {
  queued: boolean;
  reason?: 'COOLDOWN' | 'LOCKED' | 'SYNC_DISABLED' | 'NOT_SELECTABLE';
}

// ═══════════════════════════════════════════════════════════════════════════
// Uç şemaları
// ═══════════════════════════════════════════════════════════════════════════

/// `returnTo` açık-yönlendirme koruması `oauthStartSchema` ile birebir aynı
/// kalıp — iki yerde iki farklı gevşeklik olmasın.
export const destinationOAuthStartSchema = z.object({
  returnTo: z
    .string()
    .regex(/^\/dashboard(\/[\w\-/[\]]*)?$/)
    .max(200)
    .default('/dashboard/channels'),
});
export type DestinationOAuthStart = z.infer<typeof destinationOAuthStartSchema>;

export const selectMerchantAccountsSchema = z.object({
  merchantIds: z.array(z.string().min(1).max(64)).min(1).max(50),
});
export type SelectMerchantAccounts = z.infer<typeof selectMerchantAccountsSchema>;

export const registerDeveloperSchema = z.object({
  merchantAccountId: z.string().uuid(),
  /// Google'a bırakılacak teknik iletişim adresi. Opsiyonel ama şiddetle
  /// önerilir: zorunlu API duyuruları oraya gidiyor.
  developerEmail: z.email().max(200).optional(),
});
export type RegisterDeveloper = z.infer<typeof registerDeveloperSchema>;

export const publishRequestSchema = z.object({
  merchantAccountId: z.string().uuid(),
  /**
   * Yalnız bu mağazanın ürünleri. Yoksa hedefe bağlı TÜM mağazalar.
   *
   * Ayrı alan çünkü çok mağazalı bir kiracıda "önce Shopify'ı deneyeyim"
   * meşru bir istek ve alternatifi katalogun tamamını basmaktı.
   */
  storeConnectionId: z.string().uuid().optional(),
  /**
   * İlk denemede kullanılacak üst sınır. Plan gereği ilk canlı yayın
   * 10 ürünle yapılıyor — sınır sözleşmede ki arayüz de aynı sayıyı bilsin.
   */
  limit: z.coerce.number().int().min(1).max(50_000).optional(),
});
export type PublishRequest = z.infer<typeof publishRequestSchema>;

export const destinationIssueQuerySchema = listQuerySchema.extend({
  merchantAccountId: z.string().uuid().optional(),
  state: destinationItemStateSchema.optional(),
  /// Tek bir ret kodu (`missing_gtin`). Gruplanmış listeden tıklanınca.
  issueCode: z.string().trim().max(120).optional(),
});
export type DestinationIssueQuery = z.infer<typeof destinationIssueQuerySchema>;

export const destinationDisconnectSchema = z.object({
  /// Yanlış hedefi koparmayı zorlaştıran teyit — `disconnectSchema` ile aynı
  /// desen.
  confirmName: z.string().min(1).max(200),
  revokeOnPlatform: z.boolean().default(true),
  /**
   * Bizim bastığımız teklifleri hedeften SİLSİN mi.
   *
   * Varsayılan `false` ve bu bilinçli: bağlantıyı koparmak "ürünlerim
   * Google'dan kalksın" demek değil. Kullanıcı jetonunu geri çekiyor
   * olabilir, kampanyasını değil. Silmek geri alınamaz ve sessizce yapılırsa
   * ertesi gün Shopping kampanyası boşa döner.
   */
  purgeOffers: z.boolean().default(false),
});
export type DestinationDisconnect = z.infer<typeof destinationDisconnectSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Yanıt tipleri
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `GET /commerce/destinations` yanıtının TEK kartı.
 *
 * `StorePlatformCard` ve `PlatformCard` ile aynı üç durum mantığı, ama ayrı
 * tip: buradaki yetenekler yayın/durum/rapor, oradakiler reklam ve mağaza.
 * Birleştirmek, alanlarının üçte ikisi her kartta `null` olan bir tip
 * doğururdu.
 */
export interface DestinationCard {
  kind: CatalogDestinationKind;
  implemented: boolean;
  configured: boolean;
  /// ⚠️ Anahtar ADLARI, DEĞERLERİ değil.
  missingEnvKeys: string[];
  unavailableReasonKey: string | null;
  capabilities: DestinationCapabilities;
  connectionCount: number;
}

export const destinationConnectionDtoSchema = z.object({
  id: z.string(),
  kind: catalogDestinationKindSchema,
  platformUserId: z.string(),
  displayName: z.string().nullable(),
  status: connectionStatusSchema,
  /// TEMİZLENMİŞ platform hatası — ham metin jeton parçası taşıyabiliyor.
  lastError: z.string().nullable(),
  lastSyncedAt: z.string().nullable(),
  scopes: z.array(z.string()),
  accountCount: z.number().int(),
  createdAt: z.string(),
});
export type DestinationConnectionDto = z.infer<typeof destinationConnectionDtoSchema>;

export interface MerchantAccountDto {
  id: string;
  connectionId: string;
  merchantId: string;
  name: string;
  kind: MerchantAccountKind;
  websiteUrl: string | null;
  country: string | null;
  currency: string | null;
  /// Merchant Center ↔ Google Ads bağı kurulmuş mu. Shopping/PMax'in ön koşulu.
  adsLinked: boolean;
  /// `developerRegistration:registerGcp` çağrıldı mı. `false` iken her uç 401.
  developerRegistered: boolean;
  syncEnabled: boolean;
  lastSyncedAt: string | null;
  /// Bizim açtığımız data source sayısı.
  dataSourceCount: number;
}

export interface MerchantDataSourceDto {
  id: string;
  merchantAccountId: string;
  dataSourceRef: string;
  feedLabel: string;
  contentLanguage: string;
  countries: string[];
  /// Bu data source'u BİZ mi açtık. `false` olan bir kaynağa asla yazmıyoruz
  /// ve asla silmiyoruz.
  managedByUs: boolean;
  storeConnectionId: string | null;
}

/// Hedefin sağlık özeti — panelde tek uyarı kutusunda gösteriliyor.
export interface DestinationStatusDto {
  merchantAccountId: string;
  connectionStatus: ConnectionStatus;
  developerRegistered: boolean;
  websiteVerified: boolean;
  adsLinked: boolean;
  lastPublishedAt: string | null;
  lastCheckedAt: string | null;
  /// Durum kırılımı. Toplamı bizim bastığımız + hedefte gördüğümüz teklif
  /// sayısı; katalogdaki ürün sayısıyla AYNI OLMAK ZORUNDA DEĞİL ve fark
  /// tam olarak `skipped` demek.
  counts: Record<DestinationItemState, number>;
  /// En sık görülen ret kodları, azalan.
  topIssues: DestinationIssueGroup[];
}

export interface DestinationIssueGroup {
  /// Google'ın ham kodu. Arayüz `commerce.destination.issue.<code>` ile
  /// çeviriyor; anahtar yoksa ham kod basılıyor (sunucu tek dilli).
  code: string;
  count: number;
  /// Hangi destination'da (Shopping ads / Free listings). `null` = hepsinde.
  destination: string | null;
}

export interface DestinationProductRow {
  offerId: string;
  /// Katalogda karşılığı bulunamadıysa `null` — hedefte olup bizde olmayan
  /// bir teklif. Satır YİNE gösteriliyor.
  productId: string | null;
  variantId: string | null;
  title: string;
  imageUrl: string | null;
  state: DestinationItemState;
  issueCodes: string[];
  publishedByUs: boolean;
  lastPublishedAt: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hata kodları
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Arayüzün çevirdiği kodlar. `IntegrationErrorCode` ile aynı sözleşme:
 * sunucu KOD gönderir, ekran çevirir; ham sağlayıcı metni kullanıcıya
 * gitmez.
 */
export const DESTINATION_ERROR_CODE = {
  DESTINATION_NOT_AVAILABLE: 'DESTINATION_NOT_AVAILABLE',
  DESTINATION_NOT_CONFIGURED: 'DESTINATION_NOT_CONFIGURED',
  DESTINATION_CONNECTION_NOT_FOUND: 'DESTINATION_CONNECTION_NOT_FOUND',
  MERCHANT_ACCOUNT_NOT_FOUND: 'MERCHANT_ACCOUNT_NOT_FOUND',
  /**
   * GCP projesi Merchant Center'a kayıtlı değil — HER çağrı 401.
   *
   * `TOKEN_REFRESH_FAILED`ten AYRI kod olmak zorunda: ikisi de 401 üretiyor
   * ama biri "yeniden bağlan" (kullanıcı çözer), diğeri "kayıt yaptır"
   * (Merchant Center'da admin erişimi ve doğrulanmış site gerektiriyor).
   * Tek koda indirmek, kullanıcıyı sonsuza kadar yeniden bağlanmaya
   * gönderirdi.
   */
  DEVELOPER_NOT_REGISTERED: 'DEVELOPER_NOT_REGISTERED',
  /// Hesap `ADVANCED` — ürün ve rapor uçları bu tipte çalışmıyor.
  ACCOUNT_NOT_SELECTABLE: 'ACCOUNT_NOT_SELECTABLE',
  /// Hesapta doğrulanmış site yok; ürün onayı alınamaz.
  WEBSITE_NOT_VERIFIED: 'WEBSITE_NOT_VERIFIED',
  /// Yayın soğuma süresinde ya da kilitli.
  PUBLISH_THROTTLED: 'PUBLISH_THROTTLED',
  /// Yayın öncesi kuru koşu onaylanmadı.
  PREVIEW_REQUIRED: 'PREVIEW_REQUIRED',
  DESTINATION_RATE_LIMITED: 'DESTINATION_RATE_LIMITED',
  DESTINATION_PROVIDER_ERROR: 'DESTINATION_PROVIDER_ERROR',
} as const;
export type DestinationErrorCode =
  (typeof DESTINATION_ERROR_CODE)[keyof typeof DESTINATION_ERROR_CODE];
